<script setup lang="ts">
import { HotelsLangEnum } from '@revolugo/booking-api-client'
import {
  DATE_STRING_FORMAT,
  FR_DATE_STRING_FORMAT,
  Lang,
} from '@revolugo/common/constants'
import { dayjs } from '@revolugo/common/utils'
import { RAsset } from '@revolugo/design-system/atoms'
import {
  RBookingDocument,
  RContactUs,
  RDate,
  RHero,
  RPriceSummary,
} from '@revolugo/design-system/molecules'
import {
  BookingDates,
  BookingDetailsList,
  CancellationPoliciesSummary,
  HotelRoomingListEditor,
} from '@revolugo/design-system/organisms'
import { RDateDirection } from '@revolugo/design-system/types'
import {
  AccountingDocumentType,
  type HotelRoomingListGuest,
  type Invoice,
} from '@revolugo/graphql/private'
import { BookingStatus } from '@revolugo/graphql/public'

import {
  useBookingServiceStore,
  useHotelRoomingListGuestsServiceStore,
  useHotelRoomingListServiceStore,
  useHotelServiceStore,
} from '#p-stores'

import type {
  BookingAdapterProps,
  HotelAdapterProps,
  HotelRoomingListAdapterProps,
} from '#p-stores'

useBookingApi()

const router = useRouter()
const { query } = useRoute()
const { locale, t } = useI18n()

useHead({
  title: t('pageTitle'),
})

if (query.p) {
  router.push(`/${locale.value}/payment?token=${query.token}&p=${query.p}`)
}

const token = computed(() => query.token?.toString())

const booking = ref<BookingAdapterProps>()
const hotel = ref<HotelAdapterProps>()
const hotelRoomingList = ref<HotelRoomingListAdapterProps[]>()

const bookingStore = useBookingServiceStore()
const hotelStore = useHotelServiceStore()
const hotelRoomingListStore = useHotelRoomingListServiceStore()
const hotelRoomingListGuestsStore = useHotelRoomingListGuestsServiceStore()
const hotelRoomingListGuestError = ref(false)

try {
  if (token.value) {
    booking.value = await bookingStore.retrieveBooking(token.value)

    const [hotelData, hotelRoomingListData] = await Promise.all([
      hotelStore.retrieve({
        id: booking.value.hotelId,
        lang:
          locale.value === Lang.FR ? HotelsLangEnum.FrFr : HotelsLangEnum.EnUs,
      }),
      hotelRoomingListStore.list(token.value),
    ])

    hotel.value = hotelData
    hotelRoomingList.value = hotelRoomingListData
  }
} catch {
  router.push(`/${locale.value}/booking/manager/login`)
}

const hotelAsset = computed(
  () =>
    hotel.value?.hotelImages.find(image => image.isHeroImage) ??
    hotel.value?.hotelImages[0],
)

async function updateHotelRoomingListGuest({
  fullname,
  hotelRoomingListId,
  id,
}: HotelRoomingListGuest) {
  hotelRoomingListGuestError.value = false

  if (!token.value) {
    return
  }

  try {
    if (id) {
      await hotelRoomingListGuestsStore.update({
        hotelRoomingListGuestsUpdateApi: {
          fullname,
        },
        id,
        token: token.value,
      })
    } else {
      await hotelRoomingListGuestsStore.create({
        hotelRoomingListGuestsCreateApi: {
          fullname,
          hotelRoomingListId,
        },
        token: token.value,
      })
    }

    hotelRoomingList.value = await hotelRoomingListStore.list(token.value)
  } catch {
    hotelRoomingListGuestError.value = true
  }
}

async function deleteHotelRoomingListGuest(hotelRoomingListGuestId: string) {
  hotelRoomingListGuestError.value = false

  if (!token.value) {
    return
  }

  try {
    if (hotelRoomingListGuestId) {
      await hotelRoomingListGuestsStore.delete({
        id: hotelRoomingListGuestId,
        token: token.value,
      })
    }

    hotelRoomingList.value = await hotelRoomingListStore.list(token.value)
  } catch {
    hotelRoomingListGuestError.value = true
  }
}

const payModalVisible = ref(false)
const cancelModalVisible = ref(false)

function computeLabel(file: Invoice): string {
  const labelSplitted = []
  labelSplitted.push(
    file.type === AccountingDocumentType.Invoice
      ? t('documents.types.invoice')
      : t('documents.types.creditNote'),
  )

  labelSplitted.push(
    dayjs(file.createdAt).format(
      locale.value === 'fr' ? FR_DATE_STRING_FORMAT : DATE_STRING_FORMAT,
    ),
  )

  return labelSplitted.join('_')
}

const formattedFiles = computed(() =>
  booking?.value?.invoices
    ?.filter(file => file !== null)
    .map(file => ({
      label: computeLabel(file),
      link: file.url,
    })),
)

function getHeroTitle(
  status: BookingStatus,
  customer: { firstName: string; lastName: string },
) {
  switch (status) {
    case BookingStatus.Confirmed:
      return t('status.confirmed', {
        firstName: customer.firstName,
        lastName: customer.lastName,
      })
    case BookingStatus.Canceled:
      return t('status.canceled', {
        firstName: customer.firstName,
        lastName: customer.lastName,
      })
    case BookingStatus.InProgress:
      return t('status.inProgress', {
        firstName: customer.firstName,
        lastName: customer.lastName,
      })
    case BookingStatus.Created:
      return t('status.created', {
        firstName: customer.firstName,
        lastName: customer.lastName,
      })
    case BookingStatus.Failed:
      return t('status.failed', {
        firstName: customer.firstName,
        lastName: customer.lastName,
      })
    case BookingStatus.PendingConfirm:
      return t('status.pendingConfirmation', {
        firstName: customer.firstName,
        lastName: customer.lastName,
      })
    case BookingStatus.PendingPayment:
      return t('status.pendingPayment', {
        firstName: customer.firstName,
        lastName: customer.lastName,
      })
    case BookingStatus.QuoteRequest:
      return t('status.quoteRequest', {
        firstName: customer.firstName,
        lastName: customer.lastName,
      })
    default:
      return undefined
  }
}
</script>

<i18n lang="json" src="./index.i18n.json" />

<template>
  <div
    class="bg-surface-0"
    v-if="booking && hotel"
  >
    <div
      class="py-8 max-md:mx-8 md:mx-auto md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl"
    >
      <LazyBookingCancelModal
        v-if="token"
        v-model:visible="cancelModalVisible"
        :cancellationPolicies="booking.cancellationPolicies"
        :currency="booking.currency"
        :hotelName="hotel.name"
        :price="booking.hotelRoomOffer.taxIncludedPrice"
        :token="token"
        @bookingCanceled="booking = $event"
      ></LazyBookingCancelModal>
      <RHero class="rounded-lg mb-8">
        <template #content>
          <h1 class="text-2xl font-bold">
            {{ getHeroTitle(booking.status, booking.customer) }}
          </h1>
          <p>{{ t('summary') }}</p>
        </template>
      </RHero>
      <div class="lg:max-w-7xl lg:flex lg:divide-x divide-neutral-200">
        <div class="lg:flex-1 lg:pr-10">
          <RAsset
            v-if="hotelAsset"
            class="max-h-[300px] w-full object-cover mb-4 rounded"
            :image="hotelAsset"
            size="l"
            :disabled="booking?.status === BookingStatus.Canceled"
          ></RAsset>
          <BookingDates>
            <template #title>
              <h2 class="h6-semibold">
                {{ t('bookingDates.title') }}
              </h2>
            </template>
            <template #start>
              <RDate
                :date="booking.checkInDate"
                :time="hotel.checkInTime"
                :label="t('checkin')"
                :direction="RDateDirection.Start"
              ></RDate>
            </template>
            <template #end>
              <RDate
                :date="booking.checkOutDate"
                :time="hotel.checkOutTime"
                :label="t('checkout')"
                :direction="RDateDirection.End"
              ></RDate>
            </template>
          </BookingDates>
          <Divider
            type="solid"
            class="flex bg-neutral-200 h-px my-4"
          />
          <BookingDetailsList
            :booking="booking"
            :hotel="hotel"
          ></BookingDetailsList>
          <Divider
            type="solid"
            class="flex bg-neutral-200 h-px my-4"
          />
          <RPriceSummary
            :currency="booking.currency"
            :list="[
              {
                ...booking,
                descriptions: booking.hotelRoomOffer.hotelRooms.map(
                  hotelRoom => ({
                    count: hotelRoom.count,
                    description: hotelRoom.description,
                  }),
                ),
                price: booking.price,
                title: t('room', {
                  count: booking.hotelRoomOffer.hotelRooms.length,
                }),
              },
            ]"
            :total="booking.taxIncludedPrice"
            :tax-amount="booking.taxAmount"
            :title="t('priceSummary')"
            :sub-total="booking.price"
          ></RPriceSummary>
          <Divider
            type="solid"
            class="flex bg-neutral-200 h-px my-4 lg:!hidden"
          />
        </div>
        <div class="lg:flex-1 lg:pl-10">
          <CancellationPoliciesSummary
            :cancellationPolicies="booking.cancellationPolicies"
            :currency="booking.currency"
            :hotelName="hotel.name"
            :price="booking.hotelRoomOffer.taxIncludedPrice"
            :timezone="hotel.timezone"
            :bookingStatus="booking.status"
            :bookingCheckInDate="booking.checkInDate"
            @cancelBooking="cancelModalVisible = true"
            @pay-booking="payModalVisible = true"
          ></CancellationPoliciesSummary>
          <Divider
            v-if="
              hotelRoomingList?.length &&
              booking.status !== BookingStatus.Canceled
            "
            type="solid"
            class="flex bg-neutral-200 h-px my-4"
          />
          <HotelRoomingListEditor
            v-if="
              hotelRoomingList?.length &&
              booking.status !== BookingStatus.Canceled
            "
            :hotelRoomingList="hotelRoomingList"
            :adultCount="booking.adultCount"
            :error="hotelRoomingListGuestError"
            @update-hotel-rooming-list-guest="
              updateHotelRoomingListGuest($event)
            "
            @delete-hotel-rooming-list-guest="
              deleteHotelRoomingListGuest($event)
            "
          ></HotelRoomingListEditor>
          <Divider
            v-if="booking.invoices?.length"
            type="solid"
            class="flex bg-neutral-200 h-px my-4"
          />
          <RBookingDocument
            v-if="formattedFiles?.length"
            :files="formattedFiles"
          >
            <template #heading>
              <h6 class="text-xl font-medium text-neutral-950">
                {{ t('bookingDocument.title') }}
              </h6>
              <p class="mb-4 font-normal text-neutral-400">
                {{ t('bookingDocument.description') }}
              </p>
            </template>
          </RBookingDocument>
          <Divider
            type="solid"
            class="flex bg-neutral-200 h-px my-4"
          />
          <RContactUs />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
